import Phaser from 'phaser';
import { sfx } from '../audio/sfx';
import { browserStore } from './loadoutStore';
import { loadDuelMusic, toggleDuelMusic } from './duelMusicPref';
import { fxLevel, toggleFxLevel } from './fxPrefs';
import { loadVibration, toggleVibration } from './vibrationPref';
import { effectiveSoundChannels, toggleSoundChannel, type SoundChannels } from './soundPrefs';
import { stage } from './stage';
import { requestCoachReplay } from './coachCopy';
import { devMode, loadPlayFormat, savePlayFormat } from './formatPrefs';
import { loadVolumes, saveVolumes } from './volumePrefs';
import { addVolumeSlider } from './volumeSlider';
import { fitSheetCard, slideSheet } from './sheetSlide';
import { addButton, addText } from './widgets';
import { addWoodPanel, woodType } from './woodPanel';

/**
 * Title settings. Replay asks the next duel to teach again.
 * Sound and music match the pause sheet: a toggle and a volume.
 */
export class SettingsSheet {
  private readonly held: Phaser.GameObjects.GameObject[] = [];
  private readonly closers: Array<() => void> = [];
  private note = '';
  private opened = false;
  private posed = false;

  constructor(private readonly scene: Phaser.Scene) {}

  open(): void {
    this.note = '';
    this.opened = true;
    if (this.held.length > 0) {
      this.setSheetLive(true);
      const root = this.held[0];
      if (root instanceof Phaser.GameObjects.Container && !this.posed) slideSheet(this.scene, root);
      // Later repaints (replay, sound, effects) stay put. Sliding again parks Close off the phone.
      this.posed = true;
      return;
    }
    this.posed = false;
    this.paint();
    this.posed = true;
  }

  /**
   * Build the sheet once while the glen loader still covers the hub.
   * The first tap only has to show it.
   */
  prepare(): void {
    if (this.held.length === 0) {
      this.opened = false;
      this.posed = true;
      this.paint();
    }
    this.opened = false;
    this.setSheetLive(false);
    this.posed = false;
  }

  isOpen(): boolean {
    return this.opened;
  }

  close(): void {
    this.opened = false;
    this.setSheetLive(false);
  }

  private setSheetLive(on: boolean): void {
    const root = this.held[0];
    if (!(root instanceof Phaser.GameObjects.Container)) return;
    this.scene.tweens.killTweensOf(root);
    root.setVisible(on);
    const walk = (obj: Phaser.GameObjects.GameObject): void => {
      const node = obj as Phaser.GameObjects.GameObject & {
        input?: { enabled: boolean };
        list?: Phaser.GameObjects.GameObject[];
      };
      if (node.input) node.input.enabled = on;
      if (node.list) for (const child of node.list) walk(child);
    };
    walk(root);
  }

  private paint(): void {
    this.destroyHeld();
    const tight = stage.h < 1100;
    const panelW = Math.min(640, stage.w - 40);
    const panelH = Math.min(tight ? stage.h - 16 : 1060, stage.h - 16);
    const left = Math.round((stage.w - panelW) / 2);
    const top = Math.round((stage.h - panelH) / 2);
    const root = this.scene.add.container(0, 0).setDepth(90).setName('settings-sheet');
    const card = this.scene.add.container(0, 0);
    const scrim = this.scene.add.rectangle(stage.w / 2, stage.h / 2, stage.w, stage.h, 0x140c06, 0.55).setDepth(90).setInteractive();
    scrim.on('pointerup', () => {
      this.close();
    });
    const face = addWoodPanel(this.scene, left, top, panelW, panelH, 91);
    face.setInteractive();
    card.add(face);
    root.add([scrim, card]);
    this.held.push(root);
    const keep = (obj: Phaser.GameObjects.GameObject) => {
      card.add(obj);
    };
    const cx = left + panelW / 2;
    const gap = tight ? 8 : 16;
    let y = top + (tight ? 48 : 56);
    keep(addText(this.scene, cx, y, 'Settings', woodType('36px')).setOrigin(0.5).setDepth(94));
    y += tight ? 64 : 78;
    y = this.masterSound(left, y, panelW, tight);
    y += gap;
    y = this.bus(left, y, panelW, 'music', 'Music', tight);
    y += gap;
    y = this.bus(left, y, panelW, 'sfx', 'Effects', tight);
    y += gap;
    y = this.bus(left, y, panelW, 'voice', 'Voice', tight);
    y += tight ? 8 : 12;
    y = this.switches(left, y, panelW);
    y += tight ? 16 : 28;
    const replay = addButton(this.scene, cx, y, Math.min(420, panelW - 80), 'Replay the lesson', () => this.replay(), {
      fontSize: '28px',
      depth: 94,
    });
    keep(replay.slice);
    keep(replay.label);
    y += 70;
    const hint = devMode() && this.note === '' ? 'Quick is 5 rounds of 3 moves.' : this.note;
    keep(
      addText(this.scene, cx, y, hint, {
        ...woodType('24px'),
        align: 'center',
        wordWrap: { width: panelW - 80 },
      })
        .setOrigin(0.5, 0)
        .setDepth(94),
    );
    const rowY = top + panelH - 64;
    if (devMode()) {
      const quick = loadPlayFormat() === 'quick';
      const gap = 16;
      const w = Math.min(250, Math.floor((panelW - 72 - gap) / 2));
      const quickBtn = addButton(this.scene, cx - (w + gap) / 2, rowY, w, quick ? 'Quick on' : 'Quick off', () => {
        savePlayFormat(quick ? 'standard' : 'quick');
        this.scene.time.delayedCall(0, () => {
          if (this.opened) this.paint();
        });
      }, { skin: quick ? 'gold' : 'berry', fontSize: '26px', depth: 94 });
      keep(quickBtn.slice);
      keep(quickBtn.label);
      const close = addButton(this.scene, cx + (w + gap) / 2, rowY, w, 'Close', () => {
        this.close();
      }, { skin: 'berry', fontSize: '26px', depth: 94 });
      keep(close.slice);
      keep(close.label);
    } else {
      const close = addButton(this.scene, cx, rowY, 280, 'Close', () => {
        this.close();
      }, { skin: 'berry', fontSize: '28px', depth: 94 });
      keep(close.slice);
      keep(close.label);
    }
    fitSheetCard(this.scene, card, top, left, panelW, panelH);
    if (this.posed) root.y = 0;
    else {
      this.posed = true;
      slideSheet(this.scene, root);
    }
  }

  /** The hub mute button. It sits here so the glen bar can keep the gear. */
  private masterSound(left: number, y: number, panelW: number, tight: boolean): number {
    const on = !sfx.muted;
    const button = addButton(this.scene, left + panelW / 2, y, Math.min(420, panelW - 80), `Sound ${on ? 'on' : 'off'}`, () => {
      sfx.toggle();
      this.scene.time.delayedCall(0, () => {
        if (this.opened) this.paint();
      });
    }, { skin: on ? 'gold' : 'berry', fontSize: '28px', depth: 94 });
    button.slice.setName('settings-sound');
    this.keepCard(button.slice);
    this.keepCard(button.label);
    return y + (tight ? 74 : 88);
  }

  private bus(left: number, y: number, panelW: number, key: keyof SoundChannels, label: string, tight = false): number {
    const channels = effectiveSoundChannels();
    const on = channels[key];
    const button = addButton(this.scene, left + panelW / 2, y, Math.min(420, panelW - 80), `${label} ${on ? 'on' : 'off'}`, () => {
      toggleSoundChannel(key);
      this.scene.time.delayedCall(0, () => {
        if (this.opened) this.paint();
      });
    }, { skin: on ? 'gold' : 'berry', fontSize: '28px', depth: 94 });
    this.keepCard(button.slice);
    this.keepCard(button.label);
    if (key === 'voice') return y + (tight ? 76 : 88);
    const volumes = loadVolumes();
    const slider = addVolumeSlider(
      this.scene,
      left + 56,
      y + (tight ? 58 : 70),
      panelW - 112,
      key === 'music' ? volumes.music : volumes.sound,
      95,
      (value) => {
        const next = loadVolumes();
        if (key === 'music') next.music = value;
        else next.sound = value;
        saveVolumes(next);
      },
    );
    this.closers.push(() => slider.destroy());
    for (const node of slider.nodes) this.keepCard(node);
    return y + (tight ? 108 : 130);
  }

  /** Duel bed and vibration sit on one row so the sheet still clears the close button. */
  private switches(left: number, y: number, panelW: number): number {
    const gap = 16;
    const w = Math.min(280, Math.floor((panelW - 72 - gap) / 2));
    const cx = left + panelW / 2;
    const musicOn = loadDuelMusic();
    const buzzOn = loadVibration();
    const repaint = () => {
      this.scene.time.delayedCall(0, () => {
        if (this.opened) this.paint();
      });
    };
    const music = addButton(this.scene, cx - (w + gap) / 2, y, w, `Duel music ${musicOn ? 'on' : 'off'}`, () => {
      toggleDuelMusic();
      repaint();
    }, { skin: musicOn ? 'gold' : 'berry', fontSize: '24px', depth: 94 });
    const buzz = addButton(this.scene, cx + (w + gap) / 2, y, w, `Vibration ${buzzOn ? 'on' : 'off'}`, () => {
      toggleVibration();
      repaint();
    }, { skin: buzzOn ? 'gold' : 'berry', fontSize: '24px', depth: 94 });
    this.keepCard(music.slice);
    this.keepCard(music.label);
    this.keepCard(buzz.slice);
    this.keepCard(buzz.label);
    const full = fxLevel() === 'full';
    const effects = addButton(
      this.scene,
      cx,
      y + 78,
      Math.min(420, panelW - 80),
      `Board effects ${full ? 'full' : 'reduced'}`,
      () => {
        toggleFxLevel();
        repaint();
      },
      { skin: full ? 'gold' : 'berry', fontSize: '24px', depth: 94 },
    );
    effects.slice.setName('settings-effects');
    this.keepCard(effects.slice);
    this.keepCard(effects.label);
    return y + 150;
  }

  private replay(): void {
    requestCoachReplay(browserStore);
    this.note = 'It will play at the start of your next duel.';
    this.scene.time.delayedCall(0, () => {
      if (this.opened) this.paint();
    });
  }

  /** Buttons and sliders join the card, which is the last child of the sheet root. */
  private keepCard(obj: Phaser.GameObjects.GameObject): void {
    const root = this.held[0];
    const card = root instanceof Phaser.GameObjects.Container ? root.last : null;
    if (card instanceof Phaser.GameObjects.Container) card.add(obj);
  }

  private destroyHeld(): void {
    for (const close of this.closers) close();
    this.closers.length = 0;
    for (const obj of this.held) {
      this.scene.tweens.killTweensOf(obj);
      if (obj.scene) obj.destroy();
    }
    this.held.length = 0;
  }
}
